import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  approveTeamMemoryFact,
  createTeamMemoryFact,
  deleteTeamMemoryFact,
  exportTeamMemoryMarkdown,
  listTeamMemory,
  rejectTeamMemoryFact,
  updateTeamMemoryFact,
} from "../../../api";
import { useNotify } from "../../../ToastContext";
import searchIconSrc from "../../../assets/search.svg";

// Team memory (BETA-30).
//
// The plant's shared durable facts, and the queue that gates them. The agent
// never writes an active fact: it proposes, and this page is where a human
// says yes, no, or "yes, but worded like this". Approving here puts the fact
// into every teammate's next prompt, so the queue is deliberately the first
// thing on the page whenever it is non-empty.
//
// Rejecting KEEPS the body — that is the point of the 'rejected' status, not
// an oversight — so the Rejected filter is a real view, not a tombstone list.

export const TYPE_LABEL = {
  project_context: "Project context",
  workflow_pattern: "Workflow pattern",
  user_preference: "Preference",
};

export const STATUS_TABS = [
  { id: "proposed", label: "Awaiting review" },
  { id: "active", label: "Active" },
  { id: "rejected", label: "Rejected" },
  { id: "superseded", label: "Superseded" },
];

const SEARCH_DEBOUNCE_MS = 250;

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

// The gauge the caps are for. Rendered bytes, not stored bytes: what matters
// is how close the prompt block is to its budget.
export function utilizationSummary(utilization) {
  if (!utilization) return "";
  const { activeCount = 0, maxActive = 0, renderedBytes = 0, maxBytes = 0 } = utilization;
  const kb = (n) => `${Math.round((n / 1024) * 10) / 10} KB`;
  return `${activeCount} of ${maxActive} active facts · ${kb(renderedBytes)} of ${kb(maxBytes)} rendered`;
}

function FactRow({ fact, busy, onApprove, onReject, onEdit, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(fact.body);

  // A fact edited by someone else while this row was open should not keep
  // showing the stale draft once the row leaves edit mode.
  useEffect(() => {
    if (!editing) setDraft(fact.body);
  }, [editing, fact.body]);

  const save = async () => {
    const body = draft.trim();
    if (!body || body === fact.body) {
      setEditing(false);
      return;
    }
    await onEdit(fact, { body });
    setEditing(false);
  };

  return (
    <div className={`gwTmRow is-${fact.status}`} data-testid={`team-fact-${fact.id}`}>
      <div className="gwTmRowMain">
        {editing ? (
          <textarea
            className="gwTmEditor"
            aria-label={`Edit fact ${fact.id}`}
            value={draft}
            rows={3}
            onChange={(e) => setDraft(e.target.value)}
          />
        ) : (
          <div className="gwTmBody">{fact.body}</div>
        )}
        <div className="gwTmMeta">
          <span className="gwTmType">{TYPE_LABEL[fact.type] || fact.type}</span>
          {fact.assetPath ? <span className="gwTmAsset">{fact.assetPath}</span> : null}
          <span className="gwTmSource">
            {fact.source === "agent" ? "Proposed by the assistant" : `Saved by ${fact.proposedBy || "a teammate"}`}
          </span>
          <span className="gwTmDate">{formatDate(fact.createdAt)}</span>
          {fact.validUntil ? (
            <span className="gwTmExpiry">valid until {formatDate(fact.validUntil)}</span>
          ) : null}
        </div>
      </div>
      <div className="gwTmRowActions">
        {editing ? (
          <>
            <button type="button" className="gwPrimaryBtn" onClick={save} disabled={busy}>
              Save
            </button>
            <button type="button" className="gwSecondaryBtn" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </>
        ) : (
          <>
            {fact.status === "proposed" ? (
              <>
                <button
                  type="button"
                  className="gwPrimaryBtn"
                  onClick={() => onApprove(fact)}
                  disabled={busy}
                >
                  Approve
                </button>
                <button
                  type="button"
                  className="gwSecondaryBtn"
                  onClick={() => onReject(fact)}
                  disabled={busy}
                >
                  Reject
                </button>
              </>
            ) : null}
            <button type="button" className="gwSecondaryBtn" onClick={() => setEditing(true)}>
              Edit
            </button>
            <button
              type="button"
              className="gwDangerBtn"
              onClick={() => onDelete(fact)}
              disabled={busy}
            >
              Delete
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function TeamMemoryPage() {
  const notify = useNotify();
  const [status, setStatus] = useState("active");
  const [facts, setFacts] = useState([]);
  const [utilization, setUtilization] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [newBody, setNewBody] = useState("");
  const [newType, setNewType] = useState("project_context");

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const page = await listTeamMemory({ status, q: search });
      setFacts(page.facts || []);
      setUtilization(page.utilization || null);
      // The queue badge must stay honest while looking at another tab, and
      // the list response already carries the count — no second request.
      setPendingCount(page.utilization ? page.utilization.proposedCount || 0 : 0);
    } catch (err) {
      setLoadError(err.message || "Failed to load team memory");
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  useEffect(() => {
    load();
  }, [load]);

  // Every mutation reloads rather than patching local state: approving moves
  // a row between tabs and can evict another fact entirely, so a local splice
  // would show a list the server does not have.
  const run = useCallback(
    async (label, fn) => {
      setBusy(true);
      try {
        await fn();
        await load();
      } catch (err) {
        notify({ type: "error", title: "Error", message: err.message || `Failed to ${label}` });
      } finally {
        setBusy(false);
      }
    },
    [load, notify]
  );

  const onApprove = useCallback(
    (fact) =>
      run("approve the fact", async () => {
        await approveTeamMemoryFact(fact.id);
        notify({
          type: "success",
          title: "Approved",
          message: "The fact is now in everyone's assistant memory.",
        });
      }),
    [notify, run]
  );

  const onReject = useCallback(
    (fact) =>
      run("reject the fact", async () => {
        await rejectTeamMemoryFact(fact.id);
        notify({
          type: "success",
          title: "Rejected",
          message: "Kept on the Rejected tab so it is not proposed again.",
        });
      }),
    [notify, run]
  );

  const onEdit = useCallback(
    (fact, payload) => run("save the fact", () => updateTeamMemoryFact(fact.id, payload)),
    [run]
  );

  const onDelete = useCallback(
    (fact) => run("delete the fact", () => deleteTeamMemoryFact(fact.id)),
    [run]
  );

  const onAdd = useCallback(() => {
    const body = newBody.trim();
    if (!body) return;
    return run("save the fact", async () => {
      await createTeamMemoryFact({ body, type: newType });
      setNewBody("");
    });
  }, [newBody, newType, run]);

  const onExport = useCallback(async () => {
    try {
      const markdown = await exportTeamMemoryMarkdown();
      const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "team-memory.md";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      notify({ type: "error", title: "Error", message: err.message || "Failed to export" });
    }
  }, [notify]);

  const emptyMessage = useMemo(() => {
    if (search) return "No facts match that search.";
    if (status === "proposed") return "Nothing is waiting for review.";
    if (status === "active") return "The team has not saved any facts yet.";
    if (status === "rejected") return "Nothing has been rejected.";
    return "Nothing has been superseded.";
  }, [search, status]);

  return (
    <div className="gwContentGrid">
      <section className="gwFormStack">
        <div className="gwPageHeader">
          <div>
            <div className="gwPageTitle">Team memory</div>
            <div className="gwPageSubtitle">
              Durable facts the assistant carries into every teammate's conversation. The
              assistant can only propose; a fact reaches everyone once someone here approves
              it.
            </div>
          </div>
          <button type="button" className="gwSecondaryBtn" onClick={onExport}>
            Export as Markdown
          </button>
        </div>

        <div className="gwCardSection">
          <div className="gwSectionTitle gwTmListHead">
            <div className="gwTmTabs" role="tablist" aria-label="Team memory status">
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={status === tab.id}
                  className={status === tab.id ? "gwTmTab isActive" : "gwTmTab"}
                  onClick={() => setStatus(tab.id)}
                >
                  {tab.label}
                  {tab.id === "proposed" && pendingCount > 0 ? (
                    <span className="gwTmBadge">{pendingCount}</span>
                  ) : null}
                </button>
              ))}
            </div>
            <div className="gwSearch gwTmSearch">
              <img src={searchIconSrc} alt="" className="gwSearchIcon" />
              <input
                placeholder="Search facts and assets…"
                aria-label="Search team memory"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>

          <div className="gwCardSectionBody gwTmListBody">
            {loading ? (
              <div className="gwTmEmpty">Loading…</div>
            ) : loadError ? (
              <div className="gwTmError" role="alert">
                {loadError}
              </div>
            ) : facts.length === 0 ? (
              <div className="gwTmEmpty">{emptyMessage}</div>
            ) : (
              facts.map((fact) => (
                <FactRow
                  key={fact.id}
                  fact={fact}
                  busy={busy}
                  onApprove={onApprove}
                  onReject={onReject}
                  onEdit={onEdit}
                  onDelete={onDelete}
                />
              ))
            )}
          </div>

          {utilization ? (
            <div className="gwTmUtilization">{utilizationSummary(utilization)}</div>
          ) : null}
        </div>

        <div className="gwCardSection">
          <div className="gwSectionTitle">Add a fact</div>
          <div className="gwCardSectionBody gwTmAdd">
            <textarea
              className="gwTmEditor"
              aria-label="New fact"
              rows={2}
              placeholder="One concise sentence the whole team should know."
              value={newBody}
              onChange={(e) => setNewBody(e.target.value)}
            />
            <select
              aria-label="Fact type"
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
            >
              {Object.entries(TYPE_LABEL).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="gwPrimaryBtn"
              onClick={onAdd}
              disabled={busy || !newBody.trim()}
            >
              Save for the team
            </button>
          </div>
          <div className="gwTmHint">
            A fact you add here is active immediately — only the assistant's own proposals go
            through review.
          </div>
        </div>
      </section>
    </div>
  );
}

export default TeamMemoryPage;
